import { useMutation } from '@tanstack/react-query';
import { RefreshCw, RotateCcw, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { requestWithMessage } from '../lib/api';
import { useI18n } from '../lib/i18n';
import type { AiView } from '../lib/types';
import { Badge, Button, Modal, useToast, type BadgeTone } from './ui';
import { SnapshotStatus, TrustBadge, useNumber } from './kit';
import { DayRange, lastDayOf } from './a-common';

/** Still to come — nothing to press: queued, being written, or waiting for AI to be available again. */
const AI_COMING = new Set(['PENDING', 'RUNNING', 'DISABLED', 'BUDGET_EXCEEDED']);

/**
 * The one button an explanation offers — saved first (the operator's CR, 2026-11-04): a saved explanation is shown
 * and never asked for again by itself. None saved → "Ask AI to explain"; failed → "Try again" (the same request:
 * only a failure is made again); finished → "Regenerate", after a confirmation — the one explicit way to ask AI
 * again; on its way → nothing. `askPath` answers with the saved explanation when there is one, so a second
 * person asking at the same moment shares the first one's.
 */
export function AiActions({ ai, askPath, askParams, onChanged }: { ai: AiView; askPath: string; askParams?: Record<string, string | undefined>; onChanged: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const failed = (e: unknown) => toast.error(e instanceof Error ? e.message : String(e));
  const ask = useMutation({
    mutationFn: () => requestWithMessage<AiView>(askPath, { method: 'POST', params: askParams }),
    onSuccess: ({ data }) => {
      toast.success(t(data.reused ? (data.status === 'READY' ? 'aiAct.reused' : 'aiAct.inProgress') : 'aiAct.requested'));
      onChanged();
    },
    onError: failed,
  });
  const regenerate = useMutation({
    mutationFn: () => requestWithMessage<AiView>(`/executive/insights/${ai.id}/regenerate`, { method: 'POST' }),
    onSuccess: ({ data }) => {
      setConfirming(false);
      toast.success(t(data.reused ? 'aiAct.inProgress' : 'insights.regenerated'));
      onChanged();
    },
    onError: failed,
  });
  const unavailable = ai.available === false;

  if (AI_COMING.has(ai.status) || ai.status === 'NOT_CONFIGURED') return null;
  if (ai.status === 'READY' && ai.id) {
    return (
      <>
        <Button size="sm" icon={<RefreshCw className="h-4 w-4" aria-hidden />} disabled={unavailable} title={unavailable ? t('aiAct.unavailable') : undefined} onClick={() => setConfirming(true)}>
          {t('aiAct.regenerate')}
        </Button>
        <Modal
          open={confirming}
          onClose={() => setConfirming(false)}
          size="sm"
          title={t('aiAct.regenerateTitle')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                {t('common.close')}
              </Button>
              <Button variant="primary" loading={regenerate.isPending} onClick={() => regenerate.mutate()}>
                {t('aiAct.regenerateConfirm')}
              </Button>
            </>
          }
        >
          <p className="text-body-sm text-[var(--text-secondary)]">{t('aiAct.regenerateBody')}</p>
        </Modal>
      </>
    );
  }
  if (ai.status === 'FAILED' || ai.status === 'NOT_REQUESTED') {
    const retry = ai.status === 'FAILED';
    return (
      <Button
        size="sm"
        icon={retry ? <RotateCcw className="h-4 w-4" aria-hidden /> : <Sparkles className="h-4 w-4" aria-hidden />}
        loading={ask.isPending}
        disabled={unavailable}
        title={unavailable ? t('aiAct.unavailable') : undefined}
        onClick={() => ask.mutate()}
      >
        {retry ? t('aiAct.tryAgain') : t('aiAct.ask')}
      </Button>
    );
  }
  return null;
}

/**
 * Said when an explanation was written for another revision of what is shown: it is kept, not rewritten. On the
 * current revision, Regenerate is offered to ask about it; an older revision only says which one it was written for.
 */
export function WrittenForNote({ ai, revision, current = true }: { ai: AiView | null | undefined; revision: number | null | undefined; current?: boolean }) {
  const { t } = useI18n();
  if (!ai?.narrative || !ai.writtenFor || !revision || ai.writtenFor === revision) return null;
  return <p className="text-caption text-muted">{t(current ? 'aiAct.writtenFor' : 'aiAct.writtenForOnly', { revision: ai.writtenFor })}</p>;
}

/**
 * AI briefings as `GET /executive/insights` lists them (executive.controller.ts `insights`): each one an
 * explanation of a stored report, with the report it explains. Shared by AI Insights and the Overview.
 */

export interface ReportRef {
  id: string;
  kind: string;
  periodStart: string;
  /** Exclusive: the day after the last. */
  periodEnd: string;
  status: string;
  revision: number;
}

export interface InsightItem extends AiView {
  report: ReportRef | null;
}

export type AiAvailability = 'ENABLED' | 'DISABLED' | 'NOT_CONFIGURED' | 'BUDGET_EXCEEDED';

export interface InsightsList {
  availability: { state: AiAvailability | string; budget: number; used: number; estimatedCost: number | null; model: string | null };
  items: InsightItem[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** The newest daily briefing with an explanation written, if any. */
export function latestDailyBriefing(items: InsightItem[] | null | undefined): InsightItem | null {
  const daily = (items ?? []).filter((i) => i.report?.kind === 'DAILY' && i.narrative);
  daily.sort((a, b) => (b.report!.periodStart > a.report!.periodStart ? 1 : b.report!.periodStart < a.report!.periodStart ? -1 : 0));
  return daily[0] ?? null;
}

const AI_TONE: Record<string, BadgeTone> = {
  READY: 'success',
  PENDING: 'info',
  RUNNING: 'info',
  FAILED: 'error',
  DISABLED: 'neutral',
  NOT_CONFIGURED: 'neutral',
  BUDGET_EXCEEDED: 'warning',
  NOT_REQUESTED: 'neutral',
};

/** An explanation's status, in words. */
export function AiStatusBadge({ status }: { status: string | null | undefined }) {
  const { t, has } = useI18n();
  if (!status) return null;
  const key = `reports.aiStatus.${status}`;
  return <Badge tone={AI_TONE[status] ?? 'neutral'}>{has(key) ? t(key) : status}</Badge>;
}

/** A report's kind in words: Daily, Weekly, Monthly, Custom range. */
export function useKindText() {
  const { t, has } = useI18n();
  return (kind: string | null | undefined) => {
    const key = `reports.kind.${kind ?? ''}`;
    return has(key) ? t(key) : (kind ?? '—');
  };
}

/** What a stored report says about its own coverage (executive-reports.service.ts `generate`). */
export interface ReportCoverage {
  financeStatus?: string;
  financeNotices?: string[];
  peopleScored?: number;
  peopleWithoutEvidence?: number;
  evidenceTruncated?: boolean;
  healthMeasurableWeight?: number;
}

/** A report's coverage in a few words: Finance's status, who could be scored, health's measurable weight. */
export function CoverageSummary({ coverage }: { coverage: ReportCoverage | null | undefined }) {
  const { t } = useI18n();
  const num = useNumber();
  if (!coverage) return <span className="text-muted">—</span>;
  return (
    <span className="flex min-w-[12rem] flex-col gap-0.5 text-caption text-muted">
      {coverage.financeStatus && (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <TrustBadge status={coverage.financeStatus} />
          {!!coverage.financeNotices?.length && <span>{t('reports.coverage.notices', { n: num(coverage.financeNotices.length) })}</span>}
        </span>
      )}
      {coverage.peopleScored !== undefined && (
        <span>{t('reports.coverage.people', { scored: num(coverage.peopleScored), none: num(coverage.peopleWithoutEvidence ?? 0) })}</span>
      )}
      {coverage.healthMeasurableWeight !== undefined && <span>{t('reports.coverage.health', { weight: num(coverage.healthMeasurableWeight, 1) })}</span>}
      {coverage.evidenceTruncated && <span className="text-warning-fg dark:text-warning-dark">{t('reports.coverage.truncated')}</span>}
    </span>
  );
}

/** The report a briefing explains, in one line: its kind, period, status and revision. */
export function ReportRefLine({ report }: { report: ReportRef }) {
  const { t } = useI18n();
  const kind = useKindText();
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-caption text-muted">
      <span className="font-medium text-[var(--text-secondary)]">{kind(report.kind)}</span>
      <DayRange start={report.periodStart} last={lastDayOf(report.periodEnd)} />
      <SnapshotStatus status={report.status} />
      <span>{t('reports.revisionShort', { n: report.revision })}</span>
    </span>
  );
}
